/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import {
  Component,
  Input,
  OnChanges, ViewChild,
} from '@angular/core';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { availableLangs } from '../../exports';
import { ButtonSize, ButtonStyle } from '../../../aero/aero-button/aero-button.component';
import { MenuItem } from '../../menus/types';
import { MenuListPanelComponent } from '../../menus/menu-list-panel/menu-list-panel.component';

interface PageState {
  canEdit: boolean;
  isAuthenticated: boolean;
  isShareDisabled: boolean;
  type: string;
  viewType: string;
  viewPanelShowing: boolean;
  isTemplate: boolean;
  isSharedLimitReached: boolean;
}

interface Commands {
  copyPageAsTemplate: Function;
}

const loader = availableLangs.reduce((acc, lang) => {
  acc[lang] = () => import(`~/resources/translations/locale-${lang}.json`);
  return acc;
}, {});

/**
 * The components and layout in this component need to be rendered on an AJS page.
 * Angular's ng-template syntax does not work in the AngularJS context, so we need
 * to use this component to render the menus on the AJS page.
 *
 * This should be a stateless component that simply passes data from AJS to Angular.
 */
@Component({
  selector: 'bulb-page-view-header-menus',
  templateUrl: './page-view-header-menus.component.html',
  styleUrls: ['./page-view-header-menus.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: {
        scope: 'ajs',
        loader,
      },
    },
  ],
  standalone: false,
})

export class PageViewHeaderMenusComponent implements OnChanges {
  @ViewChild(MenuListPanelComponent) menuListPanelComponent: MenuListPanelComponent;

  @Input() pageState: PageState;

  @Input() unit: any;

  @Input() unitId: string;

  @Input() parentUnitId: string;

  @Input() pageMenuItems: Array<MenuItem>;

  @Input() commands: Commands;

  public buttonStyle: ButtonStyle = ButtonStyle.PRIMARY_2;

  public buttonSize: ButtonSize = ButtonSize.SLIM;

  public showShareMenu: boolean;

  public showUseTemplateButton: boolean;

  public isShareDisabled: boolean;

  public showSharePanel: boolean;

  public showSubmitButton: boolean;

  public isViewOnly: boolean;

  ngOnChanges(model) {
    if (model.pageState?.currentValue) {
      const {
        isAuthenticated, canEdit, viewPanelShowing, isShareDisabled, isTemplate, isSharedLimitReached,
      } = model.pageState.currentValue;

      const isArchivedPage = model.pageState.currentValue.type === 'ArchivePageUnit';
      const isViewOnly = this.unit.viewOnly;

      if (viewPanelShowing) {
        this.menuListPanelComponent.toggleElRef.nativeElement.click();
      }

      this.showShareMenu = isAuthenticated && canEdit;
      this.showSharePanel = isAuthenticated && canEdit && !isSharedLimitReached;
      this.showUseTemplateButton = isAuthenticated && isTemplate && !isArchivedPage;
      this.isShareDisabled = isShareDisabled;
      this.showSubmitButton = canEdit;
      this.isViewOnly = isViewOnly;
    }
  }
}
